import * as React from 'react'

import { Box, Icon, Spinner, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  DocumentsSignatureStatusEnum,
  SyncDocumentEsignatureSignatureDocument,
  SyncDocumentEsignatureSignatureMutation,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SignatureStatusTag from 'modules/documents/components/Documents/List/Item/SignatureStatusTag'
import { TIME_TO_SYNC_ESIGNATURE } from 'modules/esignatures/constants'

interface Props {
  status: DocumentsSignatureStatusEnum
  documentId: number
  requestDate?: Date
}

const DocumentSignatureStatusBox = ({ status, documentId, requestDate }: Props) => {
  const [openInfo, toggleOpenInfo] = useToggle()
  const [synchronizing, setSynchronizing] = React.useState(false)
  const [syncDocumentMutation] = useMutation(SyncDocumentEsignatureSignatureDocument, {
    extractorFn: (data: SyncDocumentEsignatureSignatureMutation) =>
      data.esignatureMutations.syncDocumentEsignatureSignature,
  })
  const signatureError = status === 'error'

  const syncSignature = async () => {
    const result = await syncDocumentMutation({
      variables: { documentId: documentId },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('documents.modal.esignature.document_signature_sync_success'),
      })

      await toggleOpenInfo(false)

      setSynchronizing(true)
      setTimeout(() => {
        setSynchronizing(false)
      }, TIME_TO_SYNC_ESIGNATURE)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Box
      component="li"
      paddingX="s16"
      paddingY="s12"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      background="grey100"
      borderRadius={{ topLeft: 'abs008', topRight: 'abs008' }}
      border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}
      id="documentSignatureStatus"
    >
      <Box flexGrow>
        <Box flexDirection="row">
          <Box alignItems="center" flexGrow flexDirection="row" gap="s12">
            <Box gap="s4">
              <Text size="300" weight="semibold" overflowWrap>
                <Box flexDirection="row" alignItems="center" gap="s4">
                  {i18n.t('documents.modal.esignature.document_signature_status_box')}

                  {!synchronizing && !signatureError && (
                    <Tooltip.CallToAction
                      attachment="bottom center"
                      targetAttachment="top center"
                      open={openInfo}
                      toggle={toggleOpenInfo}
                      title={i18n.t('documents.modal.esignature.signature_sync_info')}
                      buttonLabel={i18n.t('documents.modal.esignature.signature_sync_button')}
                      onClick={syncSignature}
                    >
                      <Box cursor="pointer" aria-label="sync-signature">
                        <Icon.Medium icon="info-circle" color="grey800" />
                      </Box>
                    </Tooltip.CallToAction>
                  )}
                  {synchronizing && (
                    <Box paddingBottom="s4">
                      <Spinner size="s16"></Spinner>
                    </Box>
                  )}
                </Box>
              </Text>
              {requestDate && (
                <Text size="100" color="grey1000" overflowWrap>
                  {i18n.t('documents.modal.esignature.document_signature_status_box_request_date', {
                    date: moment(requestDate).format('ll'),
                  })}
                </Text>
              )}
            </Box>
          </Box>
          <Box flexDirection="row" justifyContent="flexEnd">
            <Box alignSelf="center">
              <Text size="200">
                <SignatureStatusTag status={status} />
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default DocumentSignatureStatusBox
